<template>
  <div class="necessity-group__container">
    <div class="subarea-group__header">
      <p>Sub Área</p>
      <span class="gray-chip">{{ value.subAreaText ?? 'N/A' }}</span>
    </div>

    <div
      v-for="(subAreaNecessity, index) in necessityGroups"
      :key="subAreaNecessity.groupKey"
    >
      <SubAreaNecessity
        :custom-id="`sub-area-necessity-${index}`"
        :ref="`sub-area-necessity-${index}`"
        :value="subAreaNecessity"
        :hasError="hasError"
        :term-options="termOptions"
        :adequacy-options="adequacyOptions"
        :instrument-options="instrumentOptions"
        :suitability-options="suitabilityOptions"
        :necessity-options="necessityOptions"
        :is-read-only="isReadOnly"
        :skills-required="skillsRequired"
        :show-objetives-section="showObjetivesSection"
        :ai-assistant-enabled="aiAssistantEnabled"
        :auto-open-ai-objectives-goal-id="autoOpenAiObjectivesGoalId"
        :suppress-auto-open-objetive-form="suppressAutoOpenObjetiveForm"
        :sub-area-text="value.subAreaText"
        @pending-goal="setPendingGoal"
        @pending-objetive="setPendingObjetive"
        @add-sub-area-goal="relayAddSubAreaGoal"
        @add-objetive="relayAddObjetive"
        @delete-goal="$emit('delete-goal', $event)"
        @update-goal="$emit('update-goal', $event)"
        @update-objetive="$emit('update-objetive', $event)"
        @delete-objetive="$emit('delete-objetive', $event)"
        @delete-goal-necessity="$emit('delete-goal-necessity', $event)"
        @update-skills="$emit('update-skills', $event)"
        @ai-chain-goal-created="$emit('ai-chain-goal-created', $event)"
        @clear-ai-chain="$emit('clear-ai-chain')"
      />
    </div>
  </div>
</template>

<script>
import SubAreaNecessity from './SubAreaNecessity';

export default {
  name: 'SubAreaNecessityGoals',
  components: {
    SubAreaNecessity
  },
  props: {
    value: {
      type: Object,
      default: () => ({
        subAreaId: null,
        subAreaText: null,
        subAreaGoals: [],
      })
    },
    termOptions: {
      type: Array,
      default: () => []
    },
    suitabilityOptions: {
      type: Array,
      default: () => []
    },
    adequacyOptions: {
      type: Array,
      default: () => []
    },
    instrumentOptions: {
      type: Array,
      default: () => [],
    },
    hasError: {
      type: Boolean,
      default: false,
    },
    necessityOptions: {
      type: Array,
      default: () => []
    },
    skillsRequired: {
      type: Boolean,
      default: true
    },
    isReadOnly: {
      type: Boolean,
      default: false
    },
    customId: {
      type: String,
      default: 'sub-area-main'
    },
    showObjetivesSection: {
      type: Boolean,
      default: true
    },
    aiAssistantEnabled: {
      type: Boolean,
      default: false,
    },
    // AG-05 -- "Meta + Objetivos encadenados": id real (peiProgramServiceGoalId) de la meta
    // recién creada por "Meta con Asistente IA", ya no la descripción de texto -- ver
    // GoalsManager.vue.
    autoOpenAiObjectivesGoalId: {
      type: Number,
      default: null,
    },
    // 16 sep -- bug del panel "Añadir Objetivo" abierto durante alta en bloque de Metas -- este
    // componente solo reenvía el flag hacia abajo sin combinarlo con nada propio (a diferencia
    // de SubAreaNecessity.vue, que sí tiene su propio aiBulkSelectInProgress porque ahí vive el
    // alta en bloque para Sub Área existente). Ver el comentario largo en
    // SubAreaGoalCallout.vue.
    suppressAutoOpenObjetiveForm: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      pendingSubAreaNecGoals: [],
      pendingSubAreaNecObjetives: [],
    };
  },
  methods: {
    // Reenvía 'add-sub-area-goal' (burbujeado desde SubAreaNecessity.vue) hacia GoalsManager.vue
    // -- referencia de método (no un $emit('...', $event) inline) para que Vue reenvíe TODOS
    // los argumentos, incluido el callback `onCreated` del encadenado de "Meta con Asistente IA".
    relayAddSubAreaGoal(goal, onCreated) {
      this.$emit('add-sub-area-goal', goal, onCreated);
    },
    // 15 sep -- mismo motivo que relayAddSubAreaGoal de arriba: reenvía 'add-objetive'
    // (burbujeado desde SubAreaNecessity.vue -> SubAreaGoalCallout.vue) preservando el segundo
    // argumento (`onCreated`), que ahora usa "Objetivo con Asistente IA" para saber cuándo el
    // guardado real terminó y así apagar el loading de su modal de IA (ver
    // AddProgramService.handleAddObjetive / SubAreaGoalCallout.handleAiObjectiveSelected).
    relayAddObjetive(objetive, onCreated) {
      this.$emit('add-objetive', objetive, onCreated);
    },
    setPendingObjetive(data) {
      this.pendingSubAreaNecObjetives = this.pendingSubAreaNecObjetives.filter(x => x != data.customId);

      if (!!data.pending) {
        this.pendingSubAreaNecObjetives.push(data.customId);
      }
      
      const payload = {
        customId: this.customId,
        pending: data.pending
      };

      this.$emit('pending-objetive', payload);
    },
    async savePendingObjetives() {
      if(!this.pendingSubAreaNecObjetives.length) return;

      const saveObjetivePromises = [];
      
      for (let index = 0; index < this.pendingSubAreaNecObjetives.length; index++) {
        saveObjetivePromises.push(this.$refs[this.pendingSubAreaNecObjetives[index]][0].savePendingObjetive());
      }
      
      const results = await Promise.all(saveObjetivePromises);
      return results.every(value => !!value);
    },
    setPendingGoal(data) {
      this.pendingSubAreaNecGoals = this.pendingSubAreaNecGoals.filter(x => x != data.customId);

      if (!!data.pending) {
        this.pendingSubAreaNecGoals.push(data.customId);
      }
      
      const payload = {
        customId: this.customId,
        pending: data.pending
      };

      this.$emit('pending-goal', payload);
    },
    async savePendingGoals() {
      if(!this.pendingSubAreaNecGoals.length) return;

      const saveGoalPromises = [];
      
      for (let index = 0; index < this.pendingSubAreaNecGoals.length; index++) {
        saveGoalPromises.push(this.$refs[this.pendingSubAreaNecGoals[index]][0].saveGoal());
      }
      
      const results = await Promise.all(saveGoalPromises);
      return results.every(value => !!value);
    },
  },
  computed: {
    necessityGroups() {
      const strenghtAndNecessityGroups = this.value.subAreaGoals.map(x => x.strenghtsAndNecessities?.map(x => x.id)?.sort());
      const strenghtAndNecessityGroupsAsStringsSet = new Set(strenghtAndNecessityGroups.map(x => x.toString()));
      const goalsGrouped = [];

      strenghtAndNecessityGroupsAsStringsSet.forEach(groupAsString => {
        let goalGroup = this.value.subAreaGoals.filter(
          x => x.strenghtsAndNecessities
              ?.map(x => x.id)
              ?.sort().toString() === groupAsString.toString()
        );

        goalsGrouped.push({
          // 15 sep -- clave estable para el `:key` del v-for de arriba (ver por qué en el
          // comentario largo de SubAreaGoalCallout.vue / maybeAutoOpenAiObjectives): sin
          // `:key`, Vue reutiliza instancias de SubAreaNecessity/SubAreaGoalCallout por
          // POSICIÓN cuando aparece un grupo nuevo (ej. la primera Meta de una Sub Área que
          // antes no tenía ninguna) -- si ese grupo nuevo no cae al final de la lista, todo
          // lo que sigue se corre de posición, y cada instancia reutilizada conserva su
          // `data()` VIEJO (incluido `hasAutoOpenedAiObjectives`), aunque ahora esté mostrando
          // una Meta distinta. Reusamos el mismo `groupAsString` que ya arma la agrupación
          // (combinación exacta de ids de Fortaleza/Necesidad, ordenados) en vez de generar
          // un id nuevo.
          groupKey: groupAsString,
          necessityText: goalGroup[0]?.necessityText,
          necessityGoals: goalGroup
        });
      });

      return goalsGrouped;
    }
  },
};
</script>

<style scoped>
.subarea-group__header {
  margin: 10px 0 20px 0;
}

.gray-chip {
  padding: 7px 16px;
  font-weight: bold;
  border-radius: 20px;
  background-color: #ffffff;
  border: 2px #AABBB4 solid;
}
</style>
